import {
  Box,
  ButtonBase,
  FormControlLabel,
  List,
  ListItem,
  PersistentDrawer,
  Typography
} from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import type { CatalogItem } from '@/entities/catalog-item';
import getAmountLabel from '@/shared/lib/format/getAmountLabel';
import { transformCamelCaseToSpaces } from '@/shared/lib/format/transformCamelCaseToSpaces';
import Checkbox from '@/shared/ui/primitives/Checkbox';
import EllipsisWithTooltip from '@/shared/ui/primitives/EllipsisWithTooltip';
import { useFilter } from '../../../state/useFilter';

import { FILTER_DRAWER_WIDTH } from '../constants';
import useTableFilters from './useTableFilters';

import type { Table } from '@tanstack/react-table';

const buttonBaseStyle = { color: 'gx.highlight', px: 0.2 };

const TableFilters = ({
  filterOpen,
  setFilterOpen,
  table,
  expandedData,
  height
}: {
  filterOpen: boolean;
  setFilterOpen: (newFilterOpen: boolean) => void;
  table: Table<CatalogItem>;
  expandedData: boolean;
  height: string | number;
}) => {
  const { columnFiltersWithCounts } = useTableFilters(table);
  const { filters, clearAllFilters, setFilter } = useFilter();

  return (
    <PersistentDrawer
      anchor='left'
      open={filterOpen}
      sx={{
        zIndex: Z_INDEX.overlay,
        height,
        flexShrink: 0,
        fontSize: 14,
        '& .MuiDrawer-paper': {
          px: 2,
          py: 1,
          borderRight: 'none'
        }
      }}
      variant='persistent'
      onClose={() => {
        setFilterOpen(false);
      }}
    >
      <Box
        sx={{
          width: FILTER_DRAWER_WIDTH - 32,
          border: '1px solid',
          borderColor: 'gx.text',
          height: '100%',
          p: 1.5,
          pr: 1,
          backgroundColor: 'gx.tileLight',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, mr: 0.5 }}>
          <ButtonBase
            sx={buttonBaseStyle}
            onClick={clearAllFilters}
          >
            Clear all
          </ButtonBase>
        </Box>
        <Box sx={{ height: 'calc(100% - 88px)', overflow: 'auto' }}>
          <List disablePadding>
            {Object.keys(columnFiltersWithCounts).map(column => (
              <ListItem
                key={column}
                sx={{
                  backgroundColor: 'gx.tileDark',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  '&:not(:last-child)': { mb: 2 }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderBottom: '1px solid',
                    borderBottomColor: 'gx.text',
                    px: 1,
                    pt: 1.5,
                    pb: 0.5
                  }}
                >
                  {transformCamelCaseToSpaces(column)}
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column'
                  }}
                >
                  {columnFiltersWithCounts[column].map(({ label, count }) => {
                    // @ts-expect-error filter exist
                    const current = filters[column];
                    return (
                      <FormControlLabel
                        key={label}
                        control={
                          <Checkbox
                            checked={current === label}
                            onChange={() => {
                              setFilter(column, label);
                            }}
                          />
                        }
                        label={
                          <Box sx={{ maxWidth: 109, display: 'flex', alignItems: 'center' }}>
                            <EllipsisWithTooltip text={label} />
                            <Typography sx={{ ml: 0.5 }}>
                              ({count.toLocaleString('en-US')})
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    );
                  })}
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'gx.tileDark',
            p: 1,
            mt: 2,
            mb: 1.5,
            mr: 0.5
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: 'gx.highlight',
              mr: 1
            }}
          />
          {getAmountLabel(table.getRowModel().rows.length, expandedData ? 'item' : 'code')}
        </Box>
      </Box>
    </PersistentDrawer>
  );
};

export default TableFilters;
